import cookies from 'browser-cookies';
import config from '@/services/config.api';
import {
  getEmbeddedAgentBaseUrl,
  getEmbeddedAuthToken,
  getEmbeddedProductLineName,
  getEmbeddedTenantId,
} from '@/services/agentBaseUrl';
import { axiosWithHeaders } from '@innovyze/lib_get_service';
import { FetchConfig } from '@/types/chat';

const apiClient = axiosWithHeaders({
  service: 'w3fAgentServer',
  config,
  axiosCreateOpts: {
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
  },
});

apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      console.error('Unauthorized access');
    }
    return Promise.reject(error);
  },
);

export const buildFetchConfig = (
  additionalHeaders?: Record<string, string>,
): FetchConfig => {
  const baseURL = getEmbeddedAgentBaseUrl() || apiClient.defaults.baseURL || '';

  const token = getEmbeddedAuthToken() || cookies.get('auth0.user.token');
  const tenantId = getEmbeddedTenantId() || sessionStorage.getItem('tenantId');
  const productLineName =
    getEmbeddedProductLineName() || sessionStorage.getItem('productLineName');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (tenantId) {
    headers['X-Tenant-Id'] = tenantId;
  }

  if (productLineName) {
    headers['X-Product'] = productLineName;
  }

  return {
    baseURL,
    headers,
  };
};

interface FetchLogMessages {
  fail: string | ((response: Response) => string);
  error: string;
}

/**
 * Shared fetch skeleton: call `fetch`, parse the response on success, log and
 * return `fallback` on a non-ok response or a thrown error.
 */
export async function fetchJsonOrFallback<T>(
  url: string,
  init: RequestInit,
  fallback: T,
  messages: FetchLogMessages,
  parse: (response: Response) => Promise<T> = response => response.json(),
): Promise<T> {
  try {
    const response = await fetch(url, init);
    if (response.ok) {
      return await parse(response);
    }
    console.error(
      typeof messages.fail === 'function'
        ? messages.fail(response)
        : messages.fail,
    );
    return fallback;
  } catch (err) {
    console.error(`${messages.error}:`, err);
    return fallback;
  }
}
